import React from 'react';
import { UserCheck } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface OnBehalfOfNotificationSettings {
  notifyOnEntry: boolean;
  notifyOnApproval: boolean;
}

export const DEFAULT_ON_BEHALF_OF_NOTIFICATION_SETTINGS: OnBehalfOfNotificationSettings = {
  notifyOnEntry: true,
  notifyOnApproval: true,
};

interface OnBehalfOfNotificationSettingsProps {
  settings: OnBehalfOfNotificationSettings;
  onChange: (settings: OnBehalfOfNotificationSettings) => void;
}

// ---------------------------------------------------------------------------
// Setting card — identical to DelegationNotificationSettings pattern
// ---------------------------------------------------------------------------

interface SettingCardProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function SettingCard({ id, label, description, checked, onCheckedChange }: SettingCardProps) {
  return (
    <div
      className="flex items-center justify-between gap-4 p-4 rounded-lg bg-background transition-colors cursor-pointer select-none hover:bg-accent/50"
      onClick={() => onCheckedChange(!checked)}
    >
      <div className="flex-1 min-w-0">
        <Label
          htmlFor={id}
          className="text-sm font-medium leading-snug cursor-pointer select-none"
        >
          {label}
        </Label>
        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="shrink-0">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(!!v)}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OnBehalfOfNotificationSettings: React.FC<OnBehalfOfNotificationSettingsProps> = ({
  settings,
  onChange,
}) => {
  const { notifyOnEntry, notifyOnApproval } = settings;

  const handleToggle = (field: keyof OnBehalfOfNotificationSettings, checked: boolean) => {
    onChange({ ...settings, [field]: checked });
  };

  return (
    <Accordion type="multiple" defaultValue={['on-behalf-of-notifications']} className="space-y-4">
      <AccordionItem value="on-behalf-of-notifications" className="border rounded-lg">
        <AccordionTrigger className="px-4 hover:no-underline">
          <div className="flex items-center gap-3 text-left">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <UserCheck className="h-4 w-4 text-primary" />
            </div>
            <div>
              <p className="font-semibold">On Behalf Of Notifications</p>
              <p className="text-xs text-muted-foreground font-normal mt-0.5">
                Configure org-wide notifications for enter time and approval on behalf of actions
              </p>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pt-2 pb-4 space-y-6">

          {/* Notification toggles */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-foreground">On behalf of notifications</p>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <SettingCard
                id="notifyOnEntry"
                label="Notify employee when time is entered on their behalf"
                description="Sends a notification to the employee when another user submits a timesheet on their behalf"
                checked={notifyOnEntry}
                onCheckedChange={(checked) => handleToggle('notifyOnEntry', checked)}
              />
              <SettingCard
                id="notifyOnApproval"
                label="Notify approver when a timesheet is approved on their behalf"
                description="Sends a notification to the approver when another user approves a timesheet from their queue"
                checked={notifyOnApproval}
                onCheckedChange={(checked) => handleToggle('notifyOnApproval', checked)}
              />
            </div>
          </div>

          {/* Cross-link note */}
          <p className="text-xs text-muted-foreground border-t pt-4">
            These settings apply org-wide. Individual user overrides are not available at this time.
          </p>

        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
